import { GoogleAuthProvider, onIdTokenChanged, signInWithPopup, signOut, User } from 'firebase/auth';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, gql } from '../api/client';
import { Ops } from '../api/operations';
import { devBypassEnabled, getDevSession, setDevSession } from './dev-session';
import { firebaseAuth } from './firebase';
import { isStaffRole, StaffRole } from './roles';

export interface Session {
  /** staff_members.id (the `sid` claim) */
  userId: string;
  role: StaffRole;
  name?: string;
  email?: string;
  mode: 'firebase' | 'dev';
}

interface AuthState {
  session: Session | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle(): Promise<void>;
  devLogin(role: StaffRole, userId: string): void;
  logout(): Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

const NO_ACCESS = 'This Google account does not have admin console access. Ask an admin to add you.';

/** Session from the verified ID token's claims { sid, role }: the same token every subgraph checks. */
async function sessionFrom(user: User): Promise<Session | null> {
  const token = await user.getIdTokenResult();
  const { role, sid } = token.claims;
  if (!isStaffRole(role) || typeof sid !== 'string') return null;
  return { userId: sid, role, name: user.displayName ?? undefined, email: user.email ?? undefined, mode: 'firebase' };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => {
    const d = getDevSession();
    return d ? { ...d, mode: 'dev' } : null;
  });
  const [loading, setLoading] = useState(() => !getDevSession() && !!firebaseAuth());
  const [error, setError] = useState<string | null>(null);
  /** true while signInWithGoogle links the account; the first token has no role claims yet */
  const signingIn = useRef(false);

  useEffect(() => {
    const auth = firebaseAuth();
    if (!auth || getDevSession()) {
      setLoading(false);
      return;
    }
    return onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setSession(null);
        setLoading(false);
        return;
      }
      if (signingIn.current) return; // signInWithGoogle finishes the session
      const s = await sessionFrom(user);
      if (!s) {
        // e.g. access removed, or a session from before Google sign-in
        await signOut(auth);
        setSession(null);
      } else {
        setError(null);
        setSession(s);
      }
      setLoading(false);
    });
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const auth = firebaseAuth();
    if (!auth) throw new Error('Firebase is not configured');
    setError(null);
    signingIn.current = true;
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const cred = await signInWithPopup(auth, provider);
      // identity-svc matches the verified Google email to the console-access list and sets { sid, role }
      await gql('identity', Ops.staffSignIn);
      await cred.user.getIdToken(true);
      const s = await sessionFrom(cred.user);
      if (!s) throw new ApiError(NO_ACCESS, 'FORBIDDEN');
      setSession(s);
    } catch (e) {
      const err = e as ApiError & { code?: string };
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        setError(null);
      } else {
        setError(err.code === 'FORBIDDEN' ? err.message || NO_ACCESS : err.message);
      }
      if (auth.currentUser) await signOut(auth);
      setSession(null);
    } finally {
      signingIn.current = false;
      setLoading(false);
    }
  }, []);

  const devLogin = useCallback((role: StaffRole, userId: string) => {
    if (!devBypassEnabled) return;
    const s = { userId, role, name: `Dev ${role}` };
    setDevSession(s);
    setError(null);
    setSession({ ...s, mode: 'dev' });
  }, []);

  const logout = useCallback(async () => {
    setDevSession(null);
    const auth = firebaseAuth();
    if (auth?.currentUser) await signOut(auth);
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({ session, loading, error, signInWithGoogle, devLogin, logout }),
    [session, loading, error, signInWithGoogle, devLogin, logout],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
